<script setup lang="ts">
// A bottom sheet on phones, a centred dialog on wide screens. Closes on the scrim and Escape.
defineProps<{ title?: string }>()
const emit = defineEmits<{ close: [] }>()
const theme = inject<"app" | "pos">("hubTheme", "app")

function onKey(event: KeyboardEvent) {
  if (event.key === "Escape") emit("close")
}

onMounted(() => window.addEventListener("keydown", onKey))
onBeforeUnmount(() => window.removeEventListener("keydown", onKey))
</script>

<template>
  <Teleport to="body">
    <div class="scrim" :class="theme === 'pos' ? 'hub-ui hub-theme-pos' : 'hub-ui'" @click.self="emit('close')">
      <div class="sheet" role="dialog" aria-modal="true" :aria-label="title">
        <h2 v-if="title" class="sheet-title">{{ title }}</h2>
        <slot />
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.scrim {
  position: fixed;
  inset: 0;
  background: var(--hub-scrim);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 20;
  animation: fade 0.15s ease-out;
}

.sheet {
  width: 100%;
  max-width: 520px;
  max-height: 88vh;
  overflow-y: auto;
  background: var(--hub-surface);
  border-radius: 24px 24px 0 0;
  padding: 10px 12px max(16px, env(safe-area-inset-bottom));
  display: grid;
  gap: 4px;
  box-shadow: var(--hub-shadow);
  animation: rise 0.2s ease-out;
}

.sheet::before {
  content: "";
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: var(--hub-border);
  justify-self: center;
  margin-bottom: 6px;
}

@media (min-width: 600px) {
  .scrim {
    align-items: center;
    padding: 16px;
  }
  .sheet {
    border-radius: 24px;
    padding: 16px;
  }
  .sheet::before {
    display: none;
  }
}

.hub-theme-pos .sheet {
  border-radius: 0;
}

.sheet-title {
  margin: 0;
  padding: 4px 8px 8px;
  font-size: 1.1rem;
}

@keyframes rise {
  from {
    transform: translateY(24px);
    opacity: 0.6;
  }
}

@keyframes fade {
  from {
    opacity: 0;
  }
}
</style>

<style>
/* Rows inside sheets, shared by the sheets that list accounts, shops and choices. */
.hub-sheet-row {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px;
  border: 0;
  border-radius: 14px;
  background: none;
  text-align: left;
  color: var(--hub-text);
}

.hub-sheet-row:hover,
.hub-sheet-row.on {
  background: var(--hub-tint);
}

.hub-sheet-row-text {
  flex: 1;
  min-width: 0;
  display: grid;
}

.hub-sheet-row-text strong {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hub-sheet-row-text span {
  color: var(--hub-muted);
  font-size: 0.85rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
